import {
  CmsPageSection,
  PageTemplate,
  PageTemplateBlock,
  SuburbBlock,
} from '../../../../models/content';
import {
  TEMPLATE_COMPONENTS,
  TemplateKey,
  templateOrder,
  templateRecordId,
} from './suburb-template';

/**
 * What the sections editor (ContentSectionsEditorComponent) does differently when it edits a page template or one
 * suburb's inserted block (#262), and what each of those may store. Pure, so the rules are tested apart from the
 * editor.
 *   - 'page'      a CMS page (and a region's page): unchanged.
 *   - 'template'  the Suburb or Sub-region template: headings, links and data blocks, no paragraphs; the hero is first
 *                 and stays; a text block may read the suburb's About text.
 *   - 'insertion' a block on one suburb's page: any section but a hero; it reads no field of the suburb.
 *   - 'global'    a global block (#217): any section but a hero (or Service hero) or another global block.
 */
export type SectionsEditorMode = 'page' | 'template' | 'insertion' | 'global';

/** A template block's fields (the server's TEMPLATE_FIELDS): anything else with a value is refused. */
const TEMPLATE_FIELDS = [
  'type',
  'heading',
  'headingLevel',
  'align',
  'eyebrow',
  'links',
  'buttons',
  'image',
  'heroFileId',
  'imageAlt',
  'component',
  'variant',
  'bind',
  'background',
  'textColour',
  'anchor',
  'headingColour',
  'buttonAlign',
  'divider',
  'spacing',
  // component 'global-block' (#217): which global block it places.
  'globalBlock',
] as const;

/** Only the hero carries an image and buttons. */
const HERO_ONLY = new Set(['image', 'heroFileId', 'imageAlt', 'buttons']);

/** "Fills from" on a template's text block: the suburb's About text, or nothing (a heading and links). */
export const BIND_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Nothing (the heading and links only)', value: '' },
  { label: "The suburb's About text", value: 'body' },
];

type Groups = Array<{ label: string; items: Array<{ label: string; value: string }> }>;

/**
 * The building-block menu for a mode: a template offers text and the template blocks only (a global block among them,
 * #217); a page and a suburb's inserted block offer everything; a global block holds no Service hero and no global
 * block.
 */
export function componentGroupsFor(mode: SectionsEditorMode, groups: Groups): Groups {
  if (mode === 'page' || mode === 'insertion') return groups;
  const allowed =
    mode === 'template'
      ? (v: string) => ['', ...TEMPLATE_COMPONENTS].includes(v)
      : (v: string) => v !== 'global-block' && v !== 'service-hero';
  return groups
    .map((g) => ({ ...g, items: g.items.filter((o) => allowed(o.value)) }))
    .filter((g) => g.items.length > 0);
}

/** A template's hero is its page heading: it stays first and can't be removed (the server refuses both). */
export function isPinned(mode: SectionsEditorMode, section: Pick<CmsPageSection, 'type'>): boolean {
  return mode === 'template' && section.type === 'editorial-hero';
}

/** The remove button: not on a pinned hero, nor on the one block being inserted (its page's outline deletes it). */
export function isRemovable(
  mode: SectionsEditorMode,
  section: Pick<CmsPageSection, 'type'>
): boolean {
  return mode !== 'insertion' && !isPinned(mode, section);
}

/** Whether the section at `i` may move by `delta` (never past a pinned hero). */
export function canMove(
  mode: SectionsEditorMode,
  sections: ReadonlyArray<Pick<CmsPageSection, 'type'>>,
  i: number,
  delta: number
): boolean {
  const j = i + delta;
  if (j < 0 || j >= sections.length) return false;
  return !isPinned(mode, sections[i]) && !isPinned(mode, sections[j]);
}

/** What the template's FAQ and text blocks read from the suburb ('' = nothing). */
function bindFor(section: Partial<CmsPageSection>): string {
  if (section.type === 'editorial-hero') return '';
  if (section.component === 'faq-overview') return 'faqIds';
  if (!section.component) return section.bind === 'body' ? 'body' : '';
  return '';
}

const isEmpty = (v: unknown) =>
  v === undefined ||
  v === null ||
  v === '' ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0);

/**
 * A section from the editor as a template block: the template fields only (the editor shows no others in template
 * mode), images and buttons on the hero only, and the FAQ block always reading the suburb's FAQs.
 */
export function templateBlock(section: CmsPageSection): PageTemplateBlock {
  const hero = section.type === 'editorial-hero';
  const out: Record<string, unknown> = {
    _meta: { id: section._meta.id, index: section._meta.index },
  };
  for (const key of TEMPLATE_FIELDS) {
    const value =
      key === 'bind' ? bindFor(section) : (section as unknown as Record<string, unknown>)[key];
    if (isEmpty(value)) continue;
    if (!hero && HERO_ONLY.has(key)) continue;
    if (hero && key === 'component') continue;
    out[key] = value;
  }
  return out as unknown as PageTemplateBlock;
}

/** A section from the editor as a block inserted on one suburb's page, after `after`. It reads no field. */
export function insertedBlock(section: CmsPageSection, after: string): SuburbBlock {
  const { bind: _bind, ...rest } = section;
  return { ...rest, after };
}

/**
 * Indexes for the template's live blocks in their new order that keep each block's stored index where the order
 * allows. The server keeps removed blocks (tombstones) at their stored index and places suburbs' blocks anchored to
 * them by it, so renumbering every block on each save would slide those insertions to another part of the page.
 * A new or moved block takes a free index between its neighbours (or 10 past the one before).
 */
export function keepIndexes(
  ids: ReadonlyArray<string>,
  stored: ReadonlyMap<string, number>
): Map<string, number> {
  const out = new Map<string, number>();
  let prev = Number.NEGATIVE_INFINITY;
  ids.forEach((id, i) => {
    const own = stored.get(id);
    if (own !== undefined && own > prev) {
      out.set(id, own);
      prev = own;
      return;
    }
    const base = prev === Number.NEGATIVE_INFINITY ? 0 : prev;
    const next = ids
      .slice(i + 1)
      .map((x) => stored.get(x))
      .find((v): v is number => v !== undefined && v > base + 1);
    const value =
      next !== undefined && (prev === Number.NEGATIVE_INFINITY || next - base >= 2)
        ? Math.floor((base + next) / 2)
        : base + 10;
    out.set(id, value > prev ? value : base + 10);
    prev = out.get(id)!;
  });
  return out;
}

/**
 * The blocks the template editor shows: the live ones. A removed block stays stored as a tombstone (so suburbs' blocks
 * placed after it keep their place), but it isn't edited, and sending it back live would restore it.
 */
export function editableBlocks(template: PageTemplate | null): CmsPageSection[] {
  return templateOrder(template)
    .filter((t) => !t.removed)
    .map((t) => t.block as CmsPageSection);
}

/** The blocks this editor removed: live in the template it opened, gone from its sections (for the warning). */
export function removedBlocks(
  opened: PageTemplate | null,
  sections: Record<string, CmsPageSection>
): string[] {
  return templateOrder(opened)
    .filter((t) => !t.removed && !sections[t.id])
    .map((t) => t.id);
}

/**
 * A template as the editor saves it: its name and patterns, and the editor's sections as template blocks, keeping
 * stored indexes where the order allows. Blocks the editor removed are left out (the server keeps them as tombstones).
 * `current` is the template read again just before the save: a block someone else added after this editor opened is
 * sent back as it is, because the server would keep a block the save leaves out as a removed one, silently removing
 * their block from every suburb page. `removed` names those this editor removed, for the warning about suburbs' blocks
 * placed after them.
 */
export function templateForSave(
  key: TemplateKey,
  stored: { opened: PageTemplate | null; current: PageTemplate | null },
  fields: { name: string; title: string; description: string },
  sections: Record<string, CmsPageSection>
): { template: PageTemplate; removed: string[] } {
  const list = Object.values(sections).sort((a, b) => a._meta.index - b._meta.index);
  const current = stored.current ?? stored.opened;
  const storedIndex = new Map(templateOrder(current).map((t) => [t.id, t.block._meta.index]));
  const indexes = keepIndexes(
    list.map((s) => s._meta.id),
    storedIndex
  );
  const blocks: PageTemplate['blocks'] = {};
  for (const s of list) {
    const id = s._meta.id;
    blocks[id] = templateBlock({ ...s, _meta: { id, index: indexes.get(id)! } });
  }
  const seen = new Set(templateOrder(stored.opened).map((t) => t.id));
  for (const t of templateOrder(current)) {
    if (!t.removed && !seen.has(t.id) && !blocks[t.id]) blocks[t.id] = t.block;
  }
  return {
    template: {
      _meta: { id: templateRecordId(key), index: current?._meta?.index ?? 0 },
      key,
      name: fields.name.trim(),
      title: fields.title.trim(),
      description: fields.description.trim(),
      blocks,
    },
    removed: removedBlocks(stored.opened, sections),
  };
}
